// src/utils/sseStream.js
//
// Minimal client for the backend's Server-Sent Events endpoint
// (POST /video_intelligence/api/analyze/stream).
//
// Why this isn't axios or EventSource:
//   - axios (apiClient) can't read a response incrementally in the browser.
//   - EventSource can't send an Authorization header or a POST body, and
//     this endpoint needs both (Firebase Bearer token + JSON body).
// So this uses fetch() and reads response.body as a stream.

/**
 * Returns a function you feed raw text chunks to. Whenever a complete SSE
 * event ("event: name\ndata: {...}\n\n") has arrived, onEvent(name, data)
 * is called with the data JSON-parsed.
 *
 * Network chunks do not line up with events — one read can contain half an
 * event or several — so incomplete text is buffered until its blank-line
 * terminator shows up.
 */
export function createSSEParser(onEvent) {
  let buffer = ''

  return (chunk) => {
    // Normalize the whole buffer, not just the new chunk, so a "\r\n" split
    // across two chunks ("\r" | "\n") is still recognised.
    buffer = (buffer + chunk).replace(/\r\n/g, '\n')

    let boundary
    while ((boundary = buffer.indexOf('\n\n')) !== -1) {
      const block = buffer.slice(0, boundary)
      buffer = buffer.slice(boundary + 2)

      let eventName = 'message'
      const dataLines = []

      for (const line of block.split('\n')) {
        if (!line || line.startsWith(':')) continue // blank line or SSE comment
        const colon = line.indexOf(':')
        const field = colon === -1 ? line : line.slice(0, colon)
        let value = colon === -1 ? '' : line.slice(colon + 1)
        if (value.startsWith(' ')) value = value.slice(1)

        if (field === 'event') eventName = value
        else if (field === 'data') dataLines.push(value)
      }

      if (dataLines.length === 0) continue

      let data
      try {
        data = JSON.parse(dataLines.join('\n'))
      } catch (e) {
        continue // ignore a malformed event rather than killing the stream
      }
      onEvent(eventName, data)
    }
  }
}

/**
 * POSTs to an SSE endpoint and calls onEvent(name, data) for each event
 * until the server closes the stream.
 *
 * Resolves when the stream ends (normally or because the server closed it).
 * Rejects with:
 *   - an Error carrying .status and .data (parsed JSON body, if any) when
 *     the server answers with a non-2xx status. This is how validation,
 *     auth and config errors arrive — as ordinary JSON, before any stream
 *     is opened.
 *   - an AbortError when `signal` is aborted.
 *   - a network error if the connection fails.
 */
export async function streamPost({ url, token, body, signal, onEvent }) {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
    signal,
  })

  if (!response.ok) {
    let data = null
    try {
      data = await response.json()
    } catch (e) {
      // body wasn't JSON — status alone will have to do
    }
    const error = new Error(data?.message || `Request failed (${response.status})`)
    error.status = response.status
    error.data = data
    throw error
  }

  if (!response.body) {
    throw new Error('Live streaming is not supported by this browser.')
  }

  const reader = response.body.getReader()
  // stream:true keeps a multi-byte character that is split across two
  // network chunks intact instead of corrupting it.
  const decoder = new TextDecoder()
  const feed = createSSEParser(onEvent)

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    feed(decoder.decode(value, { stream: true }))
  }
  feed(decoder.decode())
}
